import { getRandomInt, questionSequence } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { latinDealer } from "../Shared/Generation/latinDealer.ts";
import { MathRun, mathPlain } from "../Shared/Pdf/mathRuns.ts";
import { formatCoefficient, formatHundredths, formatStandard, decimalMarkOf } from "../Files/Prompts/decimal.ts";
import { placeValuePrompts as en } from "../Files/Prompts/en/placeValue.ts";
import { placeValuePrompts as es } from "../Files/Prompts/es/placeValue.ts";
import { placeValuePrompts as fr } from "../Files/Prompts/fr/placeValue.ts";
import { PlaceValueOptions, ProblemData, ScientificProblem } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";

/**
 * Scientific notation, as two Place Value question types (ADR 0130).
 *
 * **Converting** (`ScientificNotation`) writes a number in scientific notation
 * or back in standard form, both directions on every page. The number is a
 * coefficient in hundredths (100 to 999, one, two or three significant digits,
 * never a trailing zero) and an exponent (3 to 9 for a large number, -7 to -2
 * for a small one), held as integers: floating point never touches either form.
 *
 * **Operating** (`ScientificOperations`) multiplies or divides two numbers in
 * scientific notation. The answer is chosen first, so its coefficient has at most
 * two decimal places (§3): a product of two tenths-or-wholes that renormalises
 * cleanly, or a quotient picked first with the dividend as its product by the
 * divisor. When the first step's coefficient is 10 or more (a product) or below
 * 1 (a quotient) the key shows both steps, `20 × 10⁸ = 2 × 10⁹`.
 *
 * **Dealing.** Each page is a Latin square (`latinDealer`) of direction by size
 * for converting, and of operation by whether the answer renormalises for
 * operating, so a page is half each way in both at once.
 *
 * Every prompt and key is `MathRun`s, because the exponent is raised
 * (`MathText`); `display` carries the same text as `mathPlain` Unicode.
 */

type Language = PrintedLanguage;
const PROMPTS: Record<Language, typeof en> = { en, es, fr };

// ─── converting ─────────────────────────────────────────────────────────────

/** Large numbers have an exponent from 3 to 9, small ones from -7 to -2 (§2). */
export const LARGE_EXPONENTS = [3, 4, 5, 6, 7, 8, 9];
export const SMALL_EXPONENTS = [-7, -6, -5, -4, -3, -2];

/**
 * A coefficient in hundredths with this many significant digits and no trailing
 * zero: 400, 450 or 405 for one, two or three. 4.50 is never drawn.
 */
export function drawCoefficient(significant: 1 | 2 | 3): number {
    const first = getRandomInt(1, 9);
    if (significant === 1) {
        return first * 100;
    }
    if (significant === 2) {
        return first * 100 + getRandomInt(1, 9) * 10;
    }
    return first * 100 + getRandomInt(0, 9) * 10 + getRandomInt(1, 9);
}

/**
 * The number `hundredths x 10^exponent` as an integer and its places after the
 * point, with no trailing zero in the decimals: 452 and 7 -> 45200000 and 0;
 * 720 and -4 -> 72 and 5.
 */
export function scaledStandard(hundredths: number, exponent: number): { scaled: number; places: number } {
    let digits = hundredths;
    let power = exponent - 2;
    while (digits % 10 === 0 && power < 0) {
        digits /= 10;
        power++;
    }
    if (power >= 0) {
        return { scaled: digits * 10 ** power, places: 0 };
    }
    return { scaled: digits, places: -power };
}

interface ConversionChoice {
    toScientific: boolean;
    large: boolean;
    significant: 1 | 2 | 3;
}

/** One page's conversions: direction by size, with the three lengths dealt evenly alongside. */
const dealDirectionBySize = latinDealer([true, false], [true, false]);

/**
 * A conversion problem from its numbers: no draw, so a page's copy can ask the
 * generator whether an example is one it prints, and what its key says. Null
 * when the numbers are outside what a sheet prints (§2): a coefficient of 1 to
 * 9.99, an exponent of 3 to 9 or -2 to -7.
 */
export function conversionProblem(
    toScientific: boolean,
    coefficient: number,
    exponent: number,
    language: Language = "en",
    key = 1
): ProblemData | null {
    const large = LARGE_EXPONENTS.includes(exponent);
    if (!large && !SMALL_EXPONENTS.includes(exponent)) return null;
    if (!Number.isInteger(coefficient) || coefficient < 100 || coefficient > 999) return null;
    // In hundredths every such number prints with no trailing zero (450 is 4.5), and has 1, 2 or 3 significant digits.
    const significant = String(coefficient).replace(/0+$/, "").length as 1 | 2 | 3;
    const t = PROMPTS[language];
    const written = formatCoefficient(coefficient, language);
    const { scaled, places } = scaledStandard(coefficient, exponent);
    const standard = formatStandard(scaled, places, language);

    const scientificForm: MathRun[] = [{ text: `${written} × 10` }, { sup: String(exponent) }];
    const prompt = toScientific ? t.toScientific(standard) : t.toStandard(written, exponent);
    const answer: MathRun[] = toScientific ? scientificForm : [{ text: standard }];
    const detail: ScientificProblem = { kind: "convert", toScientific, large, coefficient, significant, exponent };
    return {
        key,
        numbers: [],
        operator: "",
        display: [mathPlain(prompt), mathPlain(answer)],
        mathPrompt: prompt,
        mathAnswer: answer,
        scientific: detail,
    };
}

function buildConversion(choice: ConversionChoice, language: Language, key: number): ProblemData {
    const { toScientific, large, significant } = choice;
    const exponents = large ? LARGE_EXPONENTS : SMALL_EXPONENTS;
    const exponent = exponents[getRandomInt(0, exponents.length - 1)];
    const coefficient = drawCoefficient(significant);
    return conversionProblem(toScientific, coefficient, exponent, language, key)!;
}

// ─── operating ──────────────────────────────────────────────────────────────

/** A factor's exponent runs from -6 to 9, but not 0, 1 or 2 (§3). */
export const FACTOR_EXPONENTS = [-6, -5, -4, -3, -2, -1, 3, 4, 5, 6, 7, 8, 9];
/** The answer's exponent, after renormalising, runs from -9 to 12. */
export const ANSWER_EXPONENT_RANGE = { min: -9, max: 12 };

type Operation = "multiply" | "divide";

interface OperationChoice {
    operation: Operation;
    renormalised: boolean;
}

/** One page's operations: multiply or divide by whether the answer needs the second step. */
const dealOperationByStep = latinDealer<Operation, boolean>(["multiply", "divide"], [false, true]);

/** Every pair of factor exponents whose answer lands in range, by operation and whether the answer renormalises. */
function exponentPairs(operation: Operation, renormalised: boolean): [number, number][] {
    // A product renormalises up (exponent + 1), a quotient down (exponent - 1).
    const shift = renormalised ? (operation === "multiply" ? 1 : -1) : 0;
    const pairs: [number, number][] = [];
    for (const first of FACTOR_EXPONENTS) {
        for (const second of FACTOR_EXPONENTS) {
            const answer = (operation === "multiply" ? first + second : first - second) + shift;
            if (answer >= ANSWER_EXPONENT_RANGE.min && answer <= ANSWER_EXPONENT_RANGE.max) {
                pairs.push([first, second]);
            }
        }
    }
    return pairs;
}

const EXPONENT_PAIRS: Record<string, [number, number][]> = {
    "multiply:false": exponentPairs("multiply", false),
    "multiply:true": exponentPairs("multiply", true),
    "divide:false": exponentPairs("divide", false),
    "divide:true": exponentPairs("divide", true),
};

/**
 * A factor's coefficient in tenths, 11 to 99 (1.1 to 9.9, never 1): a
 * whole number half the time. A coefficient of 1 makes the coefficient step
 * trivial (`1 x 10^4` times anything), so it is left out of every factor, of a
 * divisor and (below) of a first-step quotient.
 */
function drawFactor(): number {
    return getRandomInt(0, 1) === 0 ? getRandomInt(2, 9) * 10 : getRandomInt(11, 99);
}

/**
 * The two coefficients of a problem, in tenths, and the first step's coefficient
 * in hundredths, or null when the draw is not worth printing.
 */
function drawCoefficients(
    { operation, renormalised }: OperationChoice
): { first: number; second: number; step: number } | null {
    for (let attempt = 0; attempt < 200; attempt++) {
        if (operation === "multiply") {
            const first = drawFactor();
            const second = drawFactor();
            const product = first * second; // hundredths
            if ((product >= 1000) !== renormalised) continue;
            // Renormalised, the coefficient is product / 10 hundredths: two places only if it divides.
            if (renormalised && product % 10 !== 0) continue;
            return { first, second, step: product };
        }
        // Divide: the quotient and divisor first; the dividend is their product.
        const quotient = renormalised ? getRandomInt(1, 9) : drawFactor(); // tenths; below 1 when renormalising
        const divisor = drawFactor();
        const dividend = quotient * divisor; // hundredths, must be 1 to 9.99
        if (dividend < 100 || dividend > 999) continue;
        return { first: dividend, second: divisor, step: quotient * 10 };
    }
    return null;
}

/** `5 × 10³` as runs, for the key. */
function timesTen(coefficient: string, exponent: number): MathRun[] {
    return [{ text: `${coefficient} × 10` }, { sup: String(exponent) }];
}

/**
 * An operations problem from its numbers: no draw (see `conversionProblem`). The
 * coefficients are in hundredths (`250` is 2.5). Null when a sheet would not
 * print it (§3):
 * - a multiplication's factors, or a division's divisor and quotient, are whole
 *   numbers or tenths from 1.1 to 9.9 (never 1);
 * - a product that renormalises has at most two places after it (`3.5 x 4.5`,
 *   which is 15.75, is out);
 * - a quotient is a whole number or tenths, and the dividend is from 1 to 9.99;
 * - each factor's exponent is -6 to 9 but not 0, 1 or 2, and the answer's, after
 *   renormalising, is -9 to 12.
 */
export function operationProblem(
    operation: Operation,
    first: number,
    second: number,
    firstExponent: number,
    secondExponent: number,
    language: Language = "en",
    key = 1
): ProblemData | null {
    const tenths = (hundredths: number) => (hundredths % 10 === 0 ? hundredths / 10 : NaN);
    const factorOk = (hundredths: number) => tenths(hundredths) >= 11 && tenths(hundredths) <= 99;
    if (!FACTOR_EXPONENTS.includes(firstExponent) || !FACTOR_EXPONENTS.includes(secondExponent)) return null;
    if (!factorOk(second)) return null;

    // The first step's coefficient in hundredths, and whether it has to be renormalised.
    let step: number;
    let renormalised: boolean;
    if (operation === "multiply") {
        if (!factorOk(first)) return null;
        step = tenths(first) * tenths(second);
        renormalised = step >= 1000;
        if (renormalised && step % 10 !== 0) return null;
    } else {
        if (!Number.isInteger(first) || first < 100 || first > 999) return null;
        const quotient = first / tenths(second); // tenths
        if (!Number.isInteger(quotient) || quotient < 1 || quotient > 99 || quotient === 10) return null;
        step = quotient * 10;
        renormalised = quotient < 10;
    }

    const stepExponent = operation === "multiply" ? firstExponent + secondExponent : firstExponent - secondExponent;
    // Renormalising moves the point one place: a product's 10 or more down (/10), a quotient's below 1 up (x10).
    const answerCoefficient = !renormalised ? step : operation === "multiply" ? step / 10 : step * 10;
    const answerExponent = renormalised ? stepExponent + (operation === "multiply" ? 1 : -1) : stepExponent;
    if (answerExponent < ANSWER_EXPONENT_RANGE.min || answerExponent > ANSWER_EXPONENT_RANGE.max) return null;

    const mark = decimalMarkOf(language);
    const sign = operation === "multiply" ? "×" : "÷";
    const prompt: MathRun[] = [
        { text: `(${formatHundredths(first, mark)} × 10` },
        { sup: String(firstExponent) },
        { text: `) ${sign} (${formatHundredths(second, mark)} × 10` },
        { sup: String(secondExponent) },
        { text: ") = ?" },
    ];
    const answerRuns = timesTen(formatCoefficient(answerCoefficient, language), answerExponent);
    const answer: MathRun[] = renormalised
        ? [...timesTen(formatCoefficient(step, language), stepExponent), { text: " = " }, ...answerRuns]
        : answerRuns;

    const detail: ScientificProblem = {
        kind: "operate",
        operation,
        first,
        second,
        firstExponent,
        secondExponent,
        stepCoefficient: step,
        stepExponent,
        renormalised,
        answerCoefficient,
        answerExponent,
    };
    return {
        key,
        numbers: [],
        operator: "",
        display: [mathPlain(prompt), mathPlain(answer)],
        mathPrompt: prompt,
        mathAnswer: answer,
        scientific: detail,
    };
}

function buildOperation(choice: OperationChoice, language: Language, key: number): ProblemData | null {
    const { operation, renormalised } = choice;
    const drawn = drawCoefficients(choice);
    if (drawn === null) return null;
    // About half the problems have a negative exponent somewhere (§3). Drawing
    // the pair uniformly would make it about three in four, since most pairs have one.
    const wantNegative = getRandomInt(0, 1) === 1;
    const allPairs = EXPONENT_PAIRS[`${operation}:${renormalised}`];
    const matching = allPairs.filter(([first, second]) => (first < 0 || second < 0) === wantNegative);
    const pairs = matching.length > 0 ? matching : allPairs;
    const [firstExponent, secondExponent] = pairs[getRandomInt(0, pairs.length - 1)];
    // Both numbers in hundredths: a product's factors are tenths (so x10), a quotient's
    // dividend is already the product of its quotient and divisor.
    const first = operation === "multiply" ? drawn.first * 10 : drawn.first;
    return operationProblem(operation, first, drawn.second * 10, firstExponent, secondExponent, language, key);
}

// ─── the sheet ──────────────────────────────────────────────────────────────

/** A page-shaped set of scientific-notation problems (ADR 0130), for the two new Place Value types. */
export function generateScientificNotationPages(settings: ISettingsProps): ProblemData[][] | null {
    const language = printedLanguageOf(settings);
    if (settings.PlaceValueQuestion === PlaceValueOptions.ScientificOperations) {
        const choices: OperationChoice[] = (["multiply", "divide"] as Operation[]).flatMap((operation) =>
            [false, true].map((renormalised) => ({ operation, renormalised }))
        );
        return generateProblemPages<OperationChoice>({
            settings,
            choices,
            dealPage: (count) =>
                dealOperationByStep(count).map(({ row, column }) => ({ operation: row, renormalised: column })),
            build: (choice, key) => buildOperation(choice, language, key),
            fingerprint: (problem) => problem.display![0],
        });
    }
    const choices: ConversionChoice[] = [true, false].flatMap((toScientific) =>
        [true, false].map((large) => ({ toScientific, large, significant: 1 as const }))
    );
    return generateProblemPages<ConversionChoice>({
        settings,
        choices,
        dealPage: (count) => {
            // The three lengths are dealt evenly across the page, apart from the Latin square.
            const lengths = questionSequence<1 | 2 | 3>([1, 2, 3], count);
            return dealDirectionBySize(count).map(({ row, column }, index) => ({
                toScientific: row,
                large: column,
                significant: lengths[index],
            }));
        },
        build: (choice, key) => buildConversion(choice, language, key),
        fingerprint: (problem) => problem.display![0],
    });
}
